import type { StreamHealth } from './conn-state';
import type { Integration } from '../../core/domain/integration';
import type { StreamFrame } from '../../core/bridge/types';
import type { IntegrationRowPage } from '../../core/bridge/connect-client';
import type { BackendClient } from '../bridge/backend-client';

/*
 * IntegrationManager — main-thread PROXY of the canonical manager in the Backend
 * Worker, for the 2nd integration API (DBs/brokers/REST/OT). Opens one streaming
 * subscription (a MessagePort), maintains a local cache from snapshot + deltas,
 * and re-emits `change`. Lit components subscribe via `addEventListener('change',
 * …)` — identical shape to ControllerManager.
 *
 * Unlike ControllerManager it carries NO `connState`: the gateway's data-plane
 * liveness badge is owned by the controller stream (the one connection-health
 * feed). Integrations ride the same already-proven connection, so this manager
 * is purely a roster cache. A stream error just drops the cache; the worker
 * auto-reconnects and the next snapshot refills it.
 */
export class IntegrationManager extends EventTarget {
  private readonly cache = new Map<string, Integration>();
  private port?: MessagePort;
  /** Starts `live`: this stream has no completion marker, so an empty roster and an
   *  unanswered one look alike and only a break is observable. See conn-state. */
  private state: StreamHealth = 'live';

  constructor(
    private readonly backend: BackendClient,
    private readonly gatewayId: string,
  ) {
    super();
  }

  get all(): Integration[] {
    return [...this.cache.values()];
  }

  async start(): Promise<void> {
    // A fresh subscribe drops any stale cache so a RE-start (reconnect) shows an
    // honest empty roster until the new snapshot, not the prior set.
    this.cache.clear();
    this.state = 'live';
    this.dispatchEvent(new CustomEvent('change'));
    this.port = await this.backend.api.subscribeIntegrations(this.gatewayId);
    this.port.onmessage = (e: MessageEvent<StreamFrame<Integration>>) => this.onFrame(e.data);
    this.port.start();
  }

  stop(): void {
    this.port?.postMessage({ kind: 'close' });
    this.port = undefined;
    this.cache.clear(); // a stopped manager holds no live data (frontend disconnect)
  }

  /** Whether this roster is flowing — part of what a gateway's badge claims. */
  get health(): StreamHealth {
    return this.state;
  }

  /** A READ-ONLY query on 1 integration of this gateway. The service refuses a write. */
  query(integrationId: string, statement: string, args: string[], limit: number): Promise<IntegrationRowPage['rows']> {
    return this.backend.api.integrationQuery(this.gatewayId, integrationId, statement, args, limit);
  }

  private onFrame(frame: StreamFrame<Integration>): void {
    this.state = frame.kind === 'error' ? 'down' : 'live';
    switch (frame.kind) {
      case 'snapshot':
        this.cache.clear();
        for (const i of frame.items) this.cache.set(i.id, i);
        break;
      case 'delta':
        if (frame.op === 'remove') this.cache.delete(frame.item.id);
        else this.cache.set(frame.item.id, frame.item);
        break;
      case 'error':
        // Stream broke — KEEP the cache: a transient blip must not blank the
        // integration list for the reconnect window. The worker auto-reconnects and
        // the next snapshot refills wholesale, so a stale entry can't outlive a
        // recovery. See ControllerManager.onFrame for the full rationale.
        break;
    }
    this.dispatchEvent(new CustomEvent('change'));
  }
}
